import React, { useState } from "react";
import { Container, makeStyles, Theme, createStyles, InputBase, Link, ButtonBase } from "@material-ui/core";
import { ExpandMore, Search, Warning, Close } from "@material-ui/icons";

// note: spacing here is grid gap or margins (no flexbox gap), matching the desktop app's styling

interface IFaq {
  question: string;
  answer: React.ReactNode;
  /** plain text for searching when the answer has markup */
  text?: string;
}

const SECTIONS: {title: string, items: IFaq[]}[] = [
  {title: 'Getting started', items: [
    {question: "What is PvP Levelup?",
      answer: "PvP Levelup brings balanced, tracked PvP to Age of Conan. It runs alongside the game, reads your combat log, and turns your fights into stats: kills, deaths, assists, damage, MMR and leaderboards. It has ranked matchmaking, proving grounds team matches, ranked and unranked duels, PvE groups, and an in-game overlay with a map, scoreboard, damage monitor, friends and chat."},
    {question: "How do I sign up?",
      text: "Download the app from the Home page, open it and click Sign up. Choose a username, display name and password, then enter one of your characters and its server, Fury or Crom, spelled exactly as in game. Pvplevelup sends a code to that character in game. Enter the code in the app to finish.",
      answer: <div>Download the app from the <Link href="/">Home page</Link>, open it and click <b>Sign up</b>. Choose a username, display name and password, then enter one of your characters and its server (Fury or Crom), spelled exactly as in game. <b>Pvplevelup</b> sends a code to that character in game: enter it in the app to finish.<img src="/images/codeverify.jpg" alt="Verification code in game" /></div>},
    {question: "How do I connect the app to Age of Conan?",
      text: "Open your profile in the app and click Configure app, then click into the Age of Conan window. The app finds your install, adds its scripts and interface files to the game, and starts reading your logs. You only need to do this once, or again after moving the game.",
      answer: <div>Open your <b>profile</b> in the app and click <b>Configure app</b>, then click into the Age of Conan window within 20 seconds. The app finds your install, adds its scripts and interface files to the game, and starts reading your logs. You only need to do this once (or again if you move the game).</div>},
    {question: "Why do I need to run the app as administrator?",
      text: "The app writes its scripts and interface files into your Age of Conan folder and reads the game's logs, which Windows may block otherwise. Right click the PvP Levelup icon and choose Run as administrator.",
      answer: <div>The app writes its scripts and interface files into your Age of Conan folder and reads the game's logs, which Windows may block otherwise. If configuring the app fails or nothing gets tracked, right click the PvP Levelup icon and choose <b>Run as administrator</b>.<img src="/images/administrator.jpg" alt="Run as administrator" /></div>},
    {question: "Which game client do I need?",
      answer: "The English Age of Conan client. The app reads the combat log, and only the English combat log is supported."},
  ]},
  {title: 'Tracking & the overlay', items: [
    {question: "How does the app track my games?",
      answer: "The app's scripts in Age of Conan write your character, position and target to the game's AgeOfConan.log, and the game's combat log records every hit. The app reads both and sends what it needs to the server, which works out kills, deaths, assists, finishing blows, damage and objectives. You see the results live in the app and the overlay."},
    {question: "The app says it's not tracking. How do I fix it?",
      text: "The game's scripts and combat log run when you log in to a character, from the auto_login script. Logging out and back in to the same character, or typing /logcombat off, stops them. Click Fix App Tracking: the app puts /auto_login on your clipboard, so paste it into Age of Conan chat and press Enter. Tracking comes back within a few seconds.",
      answer: <div>The game's scripts and combat log are started by Age of Conan's <b>auto_login</b> script when you log in to a character. Logging out and back in to the same character, or typing <b>/logcombat off</b>, stops them. When that happens the app shows a <b>Not tracking</b> popup. Click <b>Fix App Tracking</b>: the app copies <b>/auto_login</b> to your clipboard. Paste it into Age of Conan chat and press Enter, and tracking comes back within a few seconds. The <b>Tracking</b> chip at the top of the app shows the current status.</div>},
    {question: "What does the in-game overlay show?",
      answer: "During matches: the map with objectives, the scoreboard and timer, a breakdown of what killed you, and objective indicators for you and your team. Any time: the damage monitor (damage, healing and damage taken, with gauges and a combo breakdown), plus Friends and Chat buttons. Each part can be turned on or off on the app's Settings page, and the panels can be dragged where you like."},
    {question: "How do I share a game's results in Age of Conan chat?",
      answer: "Open the game in the app and click Show Game in Chat (or Show Duel in Chat for a duel), then click into the Age of Conan window. The app copies a chat command to your clipboard: paste it into Age of Conan chat and press Enter to post the scoreboard."},
  ]},
  {title: 'Matchmaking & characters', items: [
    {question: "How do I add my characters?",
      text: "Log in to the character in Age of Conan with the app running: the app offers to add it. Or log in to any character, log out to the character select screen, and add your characters from the Manage characters list in your profile.",
      answer: <div>Log in to the character in Age of Conan with the app running and the app offers to add it: pick its server and click <b>Add Character</b>. You can also log in to any character, log out to the character select screen, and add the ones you want from <b>Manage characters</b> on your profile.</div>},
    {question: "What is PvP verification?",
      text: "Before a character can play ranked matchmaking it has to be verified: no banned gear, AAs or buffs, and no guild. Log in to the character with the app running and it checks everything, showing what to change, then verifies the character on its own once it passes.",
      answer: <div>Before a character can queue for ranked matchmaking it has to be verified: allowed gear only, none of the banned AAs, no buffs, and no guild while verifying. Log in to the character with the app running and it checks everything, shows you exactly what to change, and verifies the character on its own once everything passes. See the <Link href="/rules">Rules</Link> for the allowed gear.</div>},
    {question: "How does ranked matchmaking work?",
      text: "Pick a server, choose one of your verified characters and ready up. Queue on your own or in a party with friends. Characters in a party have to be within 250 MMR of each other. When a match is found everyone has a short time to accept. Wins and losses move your MMR.",
      answer: <div>Pick your server, choose one of your verified characters and ready up. You can queue on your own or with friends in a party (characters in a party have to be within 250 MMR of each other). When a match is found, everyone has a short time to accept. Wins and losses move your MMR. Everyone plays by the <Link href="/rules">matchmaking rules</Link>.</div>},
  ]},
  {title: 'Proving grounds & duels', items: [
    {question: "What are the proving grounds?",
      answer: "An open world lobby in each server's zone of the day, where you can play while you wait for a ranked match or just for fun. Matches are team deathmatch, first to 50 or 4 minutes. If there was fighting in the last 2 minutes of a match, the next one starts 15 seconds later (anyone can cancel the countdown). Players with no fighting in the last 2 minutes of a match leave the lobby when it ends."},
    {question: "Can players who don't use the app join a proving grounds match?",
      answer: "Yes. Target them in game and click Add Target, in the app or on the overlay, while a match is running. Their fights are tracked from the combat logs of the players who use the app."},
    {question: "How do duels work?",
      answer: "Switch the proving grounds lobby to Dueling. Join the ranked duel queue for a best of 3 that changes your ranked duel MMR, or target someone and challenge them to an unranked duel (best of 1, 3, 5 or 7). Players who use the app are asked to accept; anyone else is dueled straight away. You can't challenge someone, or be challenged, within 30 seconds of fighting in a match."},
  ]},
  {title: 'Your account', items: [
    {question: "I forgot my password.",
      answer: "Click Reset Password on the app's login screen and enter your username and one of your characters. A code is sent to that character in game: use it to set a new password."},
    {question: "I forgot my username.",
      answer: "Click Forgot Username on the app's login screen and enter one of your characters. A code is sent to that character in game: use it to see your username."},
  ]},
];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
    },
    hero: {
      padding: theme.spacing(3.5, 3),
      marginBottom: theme.spacing(2.5),
      borderRadius: 12,
      background: 'radial-gradient(circle at 0% 0%, rgba(66, 165, 245, 0.2), transparent 55%), rgba(20, 20, 20, 0.92)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    kicker: {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      opacity: 0.6,
    },
    title: {
      fontSize: '2rem',
      fontWeight: 800,
      lineHeight: 1.2,
    },
    subtitle: {
      marginTop: 4,
      fontSize: '0.95rem',
      color: 'rgba(255, 255, 255, 0.65)',
    },
    search: {
      width: '100%',
      marginTop: theme.spacing(2.5),
      padding: theme.spacing(0.75, 1.5),
      borderRadius: 10,
      fontSize: '0.95rem',
      background: 'rgba(0, 0, 0, 0.35)',
      border: '1px solid rgba(255, 255, 255, 0.12)',
    },
    notice: {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr)',
      gridGap: theme.spacing(1.5),
      alignItems: 'start',
      marginBottom: theme.spacing(3),
      padding: theme.spacing(1.5, 2),
      borderRadius: 10,
      fontSize: '0.92rem',
      lineHeight: 1.5,
      color: '#ffe082',
      background: 'rgba(255, 202, 40, 0.08)',
      border: '1px solid rgba(255, 202, 40, 0.3)',
    },
    section: {
      margin: theme.spacing(3, 0, 1.25),
      fontSize: '0.75rem',
      fontWeight: 800,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: '#90caf9',
    },
    item: {
      marginBottom: theme.spacing(1),
      borderRadius: 10,
      overflow: 'hidden',
      background: 'rgba(20, 20, 20, 0.92)',
      border: '1px solid rgba(255, 255, 255, 0.09)',
      transition: 'border-color 0.15s',
    },
    itemOpen: {
      borderColor: 'rgba(66, 165, 245, 0.45)',
    },
    question: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gridGap: theme.spacing(2),
      alignItems: 'center',
      width: '100%',
      padding: theme.spacing(1.75, 2),
      textAlign: 'left',
      justifyContent: 'stretch',
      fontSize: '1.02rem',
      fontWeight: 700,
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.03)',
      },
    },
    chevron: {
      color: 'rgba(255, 255, 255, 0.5)',
      transition: 'transform 0.2s',
    },
    chevronOpen: {
      transform: 'rotate(180deg)',
      color: '#42a5f5',
    },
    answer: {
      padding: theme.spacing(0, 2, 2),
      fontSize: '0.95rem',
      lineHeight: 1.6,
      color: 'rgba(255, 255, 255, 0.75)',
      '& img': {
        display: 'block',
        maxWidth: '100%',
        marginTop: theme.spacing(1.5),
        borderRadius: 8,
        border: '1px solid rgba(255, 255, 255, 0.1)',
      },
      '& a': {
        color: '#64b5f6',
      },
    },
    empty: {
      padding: theme.spacing(4),
      textAlign: 'center',
      color: 'rgba(255, 255, 255, 0.55)',
    },
  }),
);

interface Props {

}

/** Frequently asked questions, grouped and searchable, each opening on click. */
export const Faq: React.FC<Props> = ({}) => {
  const classes = useStyles();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const term = search.trim().toLowerCase();
  const matches = (item: IFaq) => !term || `${item.question} ${item.text || (typeof item.answer == 'string' ? item.answer : '')}`.toLowerCase().includes(term);
  const sections = SECTIONS.map((section) => ({...section, items: section.items.filter(matches)})).filter((section) => section.items.length > 0);

  return (
    <Container maxWidth={"md"} className={classes.container}>
      <div className={classes.hero}>
        <div className={classes.kicker}>Help</div>
        <div className={classes.title}>Frequently Asked Questions</div>
        <div className={classes.subtitle}>Answers about setting up PvP Levelup, how it tracks your games, matchmaking, the proving grounds and your account.</div>
        <InputBase className={classes.search} placeholder="Search questions" value={search} onChange={(e) => setSearch(e.target.value)}
          startAdornment={<Search style={{marginRight: 8, opacity: 0.6}} />}
          endAdornment={search ? <ButtonBase onClick={() => setSearch('')} title="Clear" style={{borderRadius: '50%', padding: 2}}><Close fontSize="small" /></ButtonBase> : undefined} />
      </div>

      <div className={classes.notice}>
        <Warning />
        <span>Having trouble? Make sure you run the PvP Levelup app as administrator in Windows. See <b>"Why do I need to run the app as administrator?"</b> below.</span>
      </div>

      {sections.length == 0 && <div className={classes.empty}>No questions match "{search}"</div>}
      {sections.map((section) => (
        <div key={section.title}>
          <div className={classes.section}>{section.title}</div>
          {section.items.map((item) => {
            // a search opens every match
            const isOpen = open[item.question] == true || term.length > 0;
            return (
              <div key={item.question} className={`${classes.item} ${isOpen ? classes.itemOpen : ''}`}>
                <ButtonBase className={classes.question} onClick={() => setOpen({...open, [item.question]: !open[item.question]})} aria-expanded={isOpen}>
                  <span>{item.question}</span>
                  <ExpandMore className={`${classes.chevron} ${isOpen ? classes.chevronOpen : ''}`} />
                </ButtonBase>
                {isOpen && <div className={classes.answer}>{item.answer}</div>}
              </div>
            );
          })}
        </div>
      ))}
    </Container>
  )
}
